import { handleAxiosError } from '@/api/helpers';
import { ModifiedResource, Resource } from '@/client/courses';
import { useUpdateCourseResource } from '@/hooks/query/course-content.hooks';
import { useToast } from '@/hooks/useToast';
import { AppIconTooltip } from '@/ui/icon-tooltip';
import { FlexContainer } from '@/ui/styled-ui';
import { AppSwitch } from '@/ui/switch/AppSwitch';
import {
  convertFilesToArrayFormat,
  hasAnyResourceTranslationFile,
} from '@/utils/helpers';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

type ReadConfirmationToggleProps = {
  courseId?: string;
  resource?: ModifiedResource;
  tooltipTarget: string;
  isDisabled?: boolean;
  onUpdated: (resourceId: string, response: Resource) => void;
};

export const ReadConfirmationToggle: React.FC<ReadConfirmationToggleProps> = ({
  courseId,
  resource,
  tooltipTarget,
  isDisabled,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const toggleId = useId();
  const { updateResource, isUpdatingResource } = useUpdateCourseResource();

  const handleChange = (checked: boolean) => {
    if (!courseId || !resource?.id) return;

    const arrayShape = convertFilesToArrayFormat(resource);
    if (!arrayShape) return;

    const resourceId = resource.id;

    updateResource(
      {
        courseId,
        resourceId,
        data: {
          id: resourceId,
          title: resource.title,
          type: resource.type,
          requiresReadConfirmation: checked,
          translations: (arrayShape.translations || []).map(
            ({ language, file }) => ({ language, file }),
          ),
        },
      },
      {
        onSuccess: (response) => onUpdated(resourceId, response),
        onError: (error) => handleAxiosError(error, toast),
      },
    );
  };

  return (
    <FlexContainer
      justify="space-between"
      align="center"
      className="read-confirmation"
    >
      <FlexContainer align="center" gap="var(--xxxsmall-padding)" width="auto">
        <label htmlFor={toggleId}>
          {t('content.pdf.addCompletionCheckBox.label')}
        </label>
        <AppIconTooltip
          target={tooltipTarget}
          content={t('content.pdf.addCompletionCheckBox.tooltip')}
        />
      </FlexContainer>
      <AppSwitch
        inputId={toggleId}
        aria-label={t('content.pdf.addCompletionCheckBox.label')}
        checked={!!resource?.requiresReadConfirmation}
        disabled={
          isDisabled ||
          isUpdatingResource ||
          !hasAnyResourceTranslationFile(resource)
        }
        onChange={(e) => handleChange(!!e.value)}
      />
    </FlexContainer>
  );
};
